import { useState, useContext } from 'react';
import { Modal } from 'antd';

import { FinancialBuilderContext } from '../FinancialBuilderPage.jsx';
import { productRenderFields, industryDetails } from '../../constants.jsx';
import { genericPOSTDatabaseCall, genericDELETEDatabaseCall } from '../../helperFunctions.jsx';
import { renderFbField, PageHead, FbIcons } from '../fbUI.jsx';
import { CloseOutlined } from '@ant-design/icons';

const { ArrowRightOutlined, ArrowLeftOutlined, PlusOutlined, DeleteOutlined, SaveOutlined } = FbIcons;

async function addProductToDatabase(product) { return genericPOSTDatabaseCall('/api/fb/addProduct', { product }, 'product'); }
async function saveProductToDatabase(product) { return genericPOSTDatabaseCall('/api/fb/saveProductInfo', { product }, 'product info'); }
async function deleteProductFromDatabase(productId, companyId) { return genericDELETEDatabaseCall('/api/fb/deleteProduct', { id: productId, company_id: companyId }, 'product'); }

function pct(v) { const n = Number(v); return Number.isFinite(n) ? (n * 100).toFixed(n < 0.1 ? 1 : 0) + '%' : '—'; }
function MiniSpark({ color }) {
  const pts = [12, 19, 28, 41, 58].map((v, i) => `${(i / 4) * 220},${40 - ((v - 12) / 46) * 34 - 3}`);
  return <svg viewBox="0 0 220 40" width="100%" height="40" preserveAspectRatio="none"><polyline points={pts.join(' ')} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>;
}

export function ProductDetails({ product, setCompanySelected, messageApi, companySelected, modalView = false, useInvestorReadyFinancial = false, onDone }) {
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleInputChange = (event, prod, key) => {
    const newValue = event.target.value;
    setCompanySelected((prev) => ({ ...prev, products: prev.products.map((p) => (p.id === prod.id ? { ...p, [key]: newValue } : p)) }));
  };
  const handleInputBlur = (event, prod, key, field) => {
    let newValue = event.target.value;
    if (field.regexCondition && field.errorMessage && !field.regexCondition.test(newValue)) { messageApi.error(field.errorMessage); newValue = industryDetails[companySelected.industry][key] || field.default || ''; }
    if (newValue === '') { messageApi.error('This field cannot be empty'); newValue = industryDetails[companySelected.industry][key] || field.default || ''; }
    setCompanySelected((prev) => ({ ...prev, products: prev.products.map((p) => (p.id === prod.id ? { ...p, [key]: newValue } : p)) }));
  };
  const saveProduct = async (p) => {
    setSaving(true); messageApi.loading('Saving product info...', 0);
    try { await saveProductToDatabase(p); messageApi.destroy(); messageApi.success('Product info saved successfully'); onDone && onDone(); }
    catch (e) { console.error('Error saving product info:', e); messageApi.destroy(); messageApi.error('Failed to save product info'); }
    setSaving(false);
  };
  const deleteProduct = async (productId, companyId) => {
    setDeleting(true); messageApi.loading('Deleting product...', 0);
    try {
      await deleteProductFromDatabase(productId, companyId);
      setCompanySelected((prev) => ({ ...prev, products: prev.products.filter((p) => p.id !== productId) }));
      messageApi.destroy(); messageApi.success('Product deleted successfully'); onDone && onDone();
    } catch (e) { console.error('Error deleting product:', e); messageApi.destroy(); messageApi.error('Failed to delete product'); }
    setDeleting(false);
  };

  const offsetRenderFields = modalView ? 2 : 0;
  const financialMetrics = productRenderFields.slice(2, productRenderFields.length - 2 + offsetRenderFields).filter((item) => {
    if (useInvestorReadyFinancial) return item.key !== 'market_capture';
    return item.key !== 'revenue_allocation';
  });

  return (
    <>
      <div className="pn-fieldset">
        <div className="pn-fieldset-title">Overview</div>
        <div className="pn-fields">{productRenderFields.slice(0, 2).map((f) => renderFbField(f, product, handleInputChange, true, handleInputBlur))}</div>
      </div>
      <div className="pn-fieldset" style={{ marginTop: 18 }}>
        <div className="pn-fieldset-title">Financial assumptions</div>
        <div className="pn-fields">{financialMetrics.map((f) => renderFbField(f, product, handleInputChange, true, handleInputBlur))}</div>
      </div>
      <div className="pn-editor-foot">
        <button type="button" className="pn-btn pn-btn-danger" disabled={deleting} onClick={() => deleteProduct(product.id, product.company_id)}><DeleteOutlined /> Delete product</button>
        <div className="sp" />
        <button type="button" className="pn-btn pn-btn-accent" disabled={saving} onClick={() => saveProduct(product)}><SaveOutlined /> Save product</button>
      </div>
    </>
  );
}

function ProductCard({ product, onClick }) {
  const sub = product.revenue_model === 'Subscription Model';
  return (
    <div className="pn-card" onClick={onClick}>
      <div className="pn-card-top">
        <div><div className="pn-card-name">{product.name}</div><div className="pn-card-desc">{product.description || 'No description'}</div></div>
        <span className={"pn-badge " + (sub ? "sub" : "sales")}>{sub ? 'Subscription' : 'Sales'}</span>
      </div>
      <div className="pn-card-spark"><MiniSpark color={sub ? 'var(--pn-brand-teal)' : 'var(--pn-brand-yellow)'} /></div>
      <div className="pn-card-metrics">
        <div className="pn-metric"><div className="ml">Annual price</div><div className="mv accent">${Number(product.annual_price || 0).toLocaleString()}</div></div>
        <div className="pn-metric"><div className="ml">Growth</div><div className="mv">{pct(product.growth_rate)}</div></div>
        <div className="pn-metric"><div className="ml">Churn</div><div className="mv">{pct(product.churn_rate)}</div></div>
        <div className="pn-metric"><div className="ml">Capture</div><div className="mv">{pct(product.market_capture)}</div></div>
      </div>
    </div>
  );
}

function ProductsPage({ showNavigationControls = true }) {
  const { goTo, companySelected, setCompanySelected, messageApi } = useContext(FinancialBuilderContext);
  const [newProductName, setNewProductName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const editing = companySelected?.products.find((p) => p.id === editingId) || null;

  const addNewProduct = async () => {
    if (!newProductName) { messageApi.error('Please enter a product name'); return; }
    messageApi.loading('Adding product...', 0);
    try {
      const newProduct = await addProductToDatabase({
        name: newProductName, company_id: companySelected.id,
        growth_rate: industryDetails[companySelected.industry]['default_growth_rate'],
        growth_adjustment: industryDetails[companySelected.industry]['default_growth_adjustment'],
      });
      setCompanySelected((prev) => ({ ...prev, products: [...prev.products, newProduct] }));
      setNewProductName('');
      messageApi.destroy(); messageApi.success('Product added successfully');
    } catch (e) { console.error('Error adding product:', e); messageApi.destroy(); messageApi.error('Failed to add product'); }
  };

  return (
    <div className="pn-wrap">
      <PageHead eyebrow={companySelected.name} title="Products"
        sub={<><b>{companySelected.products.length}</b> products feeding the revenue model.</>}>
        {showNavigationControls && <>
          <button type="button" className="pn-btn pn-btn-ghost" onClick={() => goTo(1)}><ArrowLeftOutlined /> Back</button>
          <button type="button" className="pn-btn pn-btn-cta" disabled={companySelected.products.length < 1} onClick={() => goTo(3)}>
            {companySelected.products.length < 1 && <>
              Need at least 1 Product
            </>}
            {companySelected.products.length > 0 &&
              <>
                View Dashboard <ArrowRightOutlined />
              </>
            }
          </button>
        </>}
      </PageHead>

      <div className="pn-grid">
        {companySelected.products.map((p) => <ProductCard key={p.id} product={p} onClick={() => setEditingId(p.id)} />)}
        <div className="pn-add-tile" onClick={() => document.getElementById('pn-new-product')?.focus()}>
          <div className="ring"><PlusOutlined /></div><span>Add product</span>
        </div>
      </div>

      <div className="pn-addrow">
        <div className="pn-input-wrap" style={{ flex: 1 }}>
          <input id="pn-new-product" className="pn-control" placeholder="New product name" value={newProductName}
            onChange={(e) => setNewProductName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addNewProduct()} />
        </div>
        <button type="button" className="pn-btn pn-btn-ghost" onClick={addNewProduct}><PlusOutlined /> Add product</button>
      </div>

      <Modal title={editing ? `Edit ${editing.name}` : ''} open={!!editing} footer={null}
        onCancel={() => setEditingId(null)} width={"70%"} style={{ top: 20 }}>
        {editing && (
          <div className="pn-modal-embed">
            <ProductDetails product={editing} setCompanySelected={setCompanySelected} messageApi={messageApi}
              companySelected={companySelected} modalView onDone={() => setEditingId(null)} />
          </div>
        )}
      </Modal>
    </div>
  );
}

export default ProductsPage;
